/**
 * FinderTrigger.tsx — Product Fit Finder invitation
 *
 * The "enter your reg"-style pull: a control that opens the finder modal
 * (mounted once in Layout.astro). Sits naturally beside the SearchBar.
 *
 * Wording follows MAGENTO: the chain's Invitation Label / Sublabel set in
 * ProductFitFinder admin are used unless the merchant overrides them on
 * this block, so renaming a finder in Magento renames the button too. The
 * synced config is published by Layout.astro on window.__AGENTIC_FINDER__;
 * it is applied after mount (never during SSR) so the server and client
 * markup always agree — a mismatch here would be a hydration error.
 *
 * Variations (VARIATIONS tab): bar | solid | outline | compact | icon_only
 */
import React, { useEffect, useState } from 'react';
import { Crosshair } from 'lucide-react';

import type { BlockComponentProps } from '../../renderer/BlockRegistry';

interface FinderTriggerData {
  content?: {
    chain_code?: string;
    /** Legacy, IGNORED — wording comes from Magento. Kept so blocks saved
     *  before the field was removed still deserialise cleanly. */
    label?: string;
    sublabel?: string;
    variant?: string;
  };
  chain_code?: string;
  /** Legacy, ignored — see above. */
  label?: string;
  sublabel?: string;
  settings?: {
    width?: string;
    maxWidth?: string;
    margin?: string;
    variant?: string;
  };
}

interface SyncedChain {
  code: string;
  title?: string;
  invite_label?: string | null;
  invite_sublabel?: string | null;
}

function openFinderByCode(chainCode?: string) {
  window.dispatchEvent(new CustomEvent('open-finder', { detail: { chainCode: chainCode || null } }));
}

/** The chain this block points at: the chosen code, else the first synced chain. */
function pickChain(chains: SyncedChain[], wantedCode: string): SyncedChain | null {
  // No code means "offer every finder", NOT "silently use the first one".
  // Falling back to chains[0] made an unpinned launcher wear one arbitrary
  // chain's Invitation Label ("Find the right walkie talkies"), so the store's
  // Finder Button Label never got a look-in.
  if (chains.length === 0 || !wantedCode) return null;
  return chains.find(c => c.code === wantedCode) ?? null;
}

export const FinderTrigger: React.FC<BlockComponentProps & { blockId?: string }> = ({ data, styles, className, blockId }) => {
  const ftData = data as FinderTriggerData | undefined;
  const chainCode = ftData?.content?.chain_code ?? ftData?.chain_code ?? '';
  const variant = ftData?.content?.variant ?? ftData?.settings?.variant ?? 'bar';
  const { width, maxWidth, margin } = ftData?.settings ?? {};

  // Magento's wording, read after mount so SSR and hydration match.
  const [fromMagento, setFromMagento] = useState<{ label?: string; sublabel?: string }>({});
  useEffect(() => {
    const cfg = (window as unknown as { __AGENTIC_FINDER__?: { chains?: SyncedChain[]; button_label?: string } }).__AGENTIC_FINDER__;
    const chain = pickChain(Array.isArray(cfg?.chains) ? cfg.chains : [], chainCode);
    if (chain) {
      setFromMagento({
        label: (chain.invite_label || chain.title || '').trim() || undefined,
        sublabel: (chain.invite_sublabel || '').trim() || undefined,
      });
      return;
    }
    // No chain pinned — this launcher offers ALL of them, so it takes the
    // store's Finder Button Label (Stores > Configuration > ProductFitFinder)
    // rather than borrowing the wording of one arbitrary chain.
    setFromMagento({ label: (cfg?.button_label || '').trim() || undefined });
  }, [chainCode]);

  // Magento owns the wording. A pinned chain speaks with its Invitation
  // Label; an unpinned launcher speaks with the store's Finder Button Label.
  const label = fromMagento.label || 'Find Your Product Fit';
  const sublabel = fromMagento.sublabel || '';

  if (variant === 'icon_only') {
    return (
      <div className={`relative ${styles ?? ''} ${className ?? ''}`} style={{ margin }} id={blockId}>
        <button
          type="button"
          onClick={() => openFinderByCode(chainCode)}
          className="header-icon-btn p-2.5 rounded-full transition-colors flex items-center justify-center shrink-0"
          aria-label={label}
          title={label}
          aria-haspopup="dialog"
        >
          <Crosshair
            style={{
              width: 'var(--style-header-icon-size, 22px)',
              height: 'var(--style-header-icon-size, 22px)',
              // --color-header-search-icon is tuned for the SEARCH FIELD, which
              // on several stores is a white box on a dark header (CLS sets it
              // to #08014F). Using it here painted a near-black icon on a dark
              // pill. This is a .header-icon-btn, so follow the same token the
              // wishlist / quote / bag icons use.
              color: 'var(--color-finder-trigger-icon, var(--color-header-icon, currentColor))',
            }}
          />
        </button>
      </div>
    );
  }

  // Header tokens, NOT the page-body ones. --color-surface/--color-text are
  // page tokens: inside a coloured header they gave white-on-white in light
  // mode (live finding). These are the same tokens MiniCart and the search
  // control use, so the trigger follows whichever style preset is active and
  // flips correctly between light and dark.
  const accent = 'var(--color-finder-trigger-accent, var(--accent-primary, var(--color-primary, currentColor)))';
  const headerPill: React.CSSProperties = {
    borderColor: 'var(--color-finder-trigger-border, var(--color-header-icon-border, var(--color-header-search-border, var(--glass-border, rgba(255,255,255,0.18)))))',
    backgroundColor: 'var(--color-finder-trigger-bg, var(--color-header-icon-bg, var(--color-header-search-bg, rgba(255,255,255,0.06))))',
    // inherit the header's own text colour rather than the page body's
    color: 'var(--color-finder-trigger-text, var(--color-header-text, currentColor))',
  };
  const shape: Record<string, React.CSSProperties> = {
    bar: headerPill,
    compact: headerPill,
    solid: {
      borderColor: accent,
      backgroundColor: accent,
      color: 'var(--color-button-text, #fff)',
    },
    outline: {
      borderColor: accent,
      backgroundColor: 'transparent',
      color: accent,
    },
  };
  const isCompact = variant === 'compact';

  return (
    <div className={`relative ${styles ?? ''} ${className ?? ''}`} style={{ width, maxWidth, margin }} id={blockId}>
      <button
        type="button"
        onClick={() => openFinderByCode(chainCode)}
        className={`fitfinder-trigger group flex w-full items-center border text-left transition-all hover:shadow-sm ${
          isCompact ? 'gap-2 px-3 py-1.5' : 'gap-2.5 px-4 py-2.5'
        }`}
        style={{
          // follow the chosen style preset's button radius, like every other
          // header control, instead of hard-coding a pill
          borderRadius: 'var(--style-btn-radius, 9999px)',
          ...(shape[variant] ?? shape.bar),
        }}
        aria-label={label}
        aria-haspopup="dialog"
      >
        <Crosshair
          className="shrink-0 opacity-70 group-hover:opacity-100 transition-opacity"
          style={{
            width: isCompact ? '16px' : 'var(--style-header-icon-size, 18px)',
            height: isCompact ? '16px' : 'var(--style-header-icon-size, 18px)',
            // The pill sits on the header chrome, not inside the search field,
            // so its icon follows --color-header-icon (what every other header
            // icon uses) and falls back to the pill's own text colour.
            color: variant === 'bar' || variant === 'compact'
              ? 'var(--color-finder-trigger-icon, var(--color-header-icon, var(--color-finder-trigger-text, currentColor)))'
              : 'currentColor',
          }}
        />
        <span className="flex min-w-0 flex-col">
          <span className={`truncate font-semibold leading-tight ${isCompact ? 'text-xs' : 'text-sm'}`}>{label}</span>
          {sublabel && !isCompact && (
            <span className="truncate text-xs opacity-60 leading-tight">{sublabel}</span>
          )}
        </span>
      </button>
    </div>
  );
};

export default FinderTrigger;
